import { Alert, Card, Col, Link, PageHeader, Row, Table, Text, Tooltip } from '@app/components'
import { QuestionCircleOutlined } from '@app/components/icons'
import { IStore } from '@app/redux'
import { auth, Axios, errorHandler } from '@app/services'
import { getThemeColors } from '@app/theme'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { ReferralsWalletForm } from './ReferralsWalletForm'

type LeaderboardEntry = {
  referralCode: string
  referralLeadsCount: number
  referralConversionsCount: number
}

type LeaderboardData = {
  ranking: number
  top: LeaderboardEntry[]
  above: LeaderboardEntry[]
  below: LeaderboardEntry[]
}

const limit = 3

const StyledTable = styled(Table)<{ highlightColor: string }>`
  tr.leaderboard-row-self {
    background-color: ${(props) => props.highlightColor};
    font-weight: bold;
    td {
      &.ant-table-cell.ant-table-cell-row-hover {
        background-color: ${(props) => props.highlightColor};
      }
    }
  }
`

function Leaderboard({ user }: { user: IStore['user'] }) {
  const { uid, referralCode, referralLeadsCount, referralConversionsCount } = user

  const [leaderboard, setLeaderboard] = useState<LeaderboardData>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { theme } = useSelector((state: IStore) => state)
  const colors = getThemeColors(theme.value)
  const hihglightColor = colors.tableHighlight

  useEffect(() => {
    const fetch = async () => {
      try {
        const authorization = await auth.currentUser.getIdToken()
        const headers = {
          Authorization: authorization,
        }

        const { data } = await Axios.get('/api/referrals/leaderboard', {
          params: {
            uid,
            limit,
            orderBy: 'referralLeadsCount',
          },
          headers,
        })
        setLoading(false)
        setLeaderboard(data)
      } catch (error) {
        setError(error)
        errorHandler.report`Failed to fetch referrals leaderboard: ${error}`
      }
    }
    void fetch()
  }, [uid])

  const leaderboardTableData = useMemo(() => {
    if (!leaderboard) {
      return []
    }

    let aboveStartsAt = 0
    let belowStartsAt = 0
    if (leaderboard.ranking <= limit * 2) {
      aboveStartsAt = Math.min(limit * 2 - leaderboard.ranking + 1, leaderboard.above.length)
    }
    if (leaderboard.ranking <= limit) {
      belowStartsAt = limit - leaderboard.ranking
    }
    const showSelf = leaderboard.ranking >= leaderboard.top.length
    const hasGap = leaderboard.ranking > limit * 2 + 1

    const gap = { ranking: '...', referralCode: '...', referralLeadsCount: '...', referralConversionsCount: '...' }

    return [
      ...leaderboard.top.map(({ referralCode, referralLeadsCount, referralConversionsCount }, idx) => ({
        ranking: idx + 1,
        referralCode,
        referralLeadsCount,
        referralConversionsCount,
      })),
      ...(hasGap ? [gap] : []),
      ...leaderboard.above
        .slice(aboveStartsAt)
        .map(({ referralCode, referralLeadsCount, referralConversionsCount }, idx) => ({
          ranking: leaderboard.ranking - leaderboard.above.length + idx + aboveStartsAt,
          referralCode,
          referralLeadsCount,
          referralConversionsCount,
        })),
      ...(showSelf
        ? [
          {
            ranking: leaderboard.ranking,
            referralCode,
            referralLeadsCount,
            referralConversionsCount,
          },
        ]
        : []),
      ...leaderboard.below
        .slice(belowStartsAt)
        .map(({ referralCode, referralLeadsCount, referralConversionsCount }, idx) => ({
          ranking: leaderboard.ranking + 1 + belowStartsAt + idx,
          referralCode,
          referralLeadsCount,
          referralConversionsCount,
        })),
      gap,
    ]
  }, [leaderboard])

  return (
    <>
      {!loading && error && <Text>Something went wrong. Please try again later</Text>}
      {!error && (
        <Card title="Leaderboard" bodyStyle={{ padding: 0 }}>
          <StyledTable
            loading={loading}
            columns={[
              { dataIndex: 'ranking', title: '' },
              {
                dataIndex: 'referralCode',
                title: 'Referral code',
                render: (code) => (code === referralCode ? <>You - {code}</> : code),
              },
              { dataIndex: 'referralLeadsCount', title: 'Referrals joined' },
              { dataIndex: 'referralConversionsCount', title: 'Referrals converted' },
            ]}
            rowClassName={(data) => {
              return data.referralCode === referralCode ? 'leaderboard-row-self' : ''
            }}
            dataSource={leaderboardTableData}
            pagination={false}
            highlightColor={hihglightColor}
          />
        </Card>
      )}
    </>
  )
}

export default function Referrals() {
  const { user } = useSelector((state: IStore) => state)

  const { referralCode, referralLeadsCount, referralConversionsCount, referralWaitEarned, referralCSTEarned } =
    user as any

  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  return (
    <Row gutter={[16, 24]}>
      <Col span={24}>
        <PageHeader disableBreadcrumb />
        <Text>
          Refer a friend and earn <b>20 CST</b> for each friend who signs up using your link and completes their first
          transaction (converts).
        </Text>
        <div style={{ height: 8 }} />
        <Text type="secondary">
          If you were referred by someone else, once you convert you’ll receive a{' '}
          <a href="https://learn.0xcoast.com" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
            1-year lock of 100 CST
          </a>{' '}
          to your payout wallet.
        </Text>
      </Col>

      {referralCode && (
        <>
          <Col span={24}>
            <Card title="Share your referral link">
              <Link href={`${origin}/invites/${referralCode}`}>
                <Text copyable>{`${origin}/invites/${referralCode}`}</Text>
              </Link>
            </Card>
          </Col>

          <Col span={24}>
            <Leaderboard user={user} />
          </Col>

          <Col xs={24} md={12} style={{ display: 'flex' }}>
            <Card
              title="Your referrals"
              style={{ width: '100%', height: '100%' }}
              extra={
                <Tooltip title="Only referrals who have completed their first transaction are considered converted.">
                  <QuestionCircleOutlined />
                </Tooltip>
              }
            >
              <Text>
                You have referred {referralLeadsCount || 0} friend
                {referralLeadsCount !== 1 ? 's' : ''}
                {referralConversionsCount > 0 && <>, {referralConversionsCount} of them converted</>}
              </Text>
            </Card>
          </Col>

          <Col xs={24} md={12} style={{ display: 'flex' }}>
            <Card
              title="Your earnings"
              style={{ width: '100%', height: '100%' }}
              extra={
                <Tooltip title="Converted referrals earn you CST. Please allow a few days for us to process payouts. Legacy WAIT earnings are still shown here.">
                  <QuestionCircleOutlined />
                </Tooltip>
              }
            >
              <Text>
                <b>Legacy:</b> We&apos;ve sent you {referralWaitEarned || 0} WAIT
              </Text>

              <div style={{ height: 8 }} />

              <Text>
                <b>New:</b> We&apos;ve sent you {referralCSTEarned || 0} CST
              </Text>
            </Card>
          </Col>

          <Col xs={24} md={24}>
            <Card title="Your wallet address for referral payouts">
              <ReferralsWalletForm user={user} />
            </Card>
          </Col>
        </>
      )}

      {!referralCode && (
        <Col span={24}>
          <Alert
            message="You must complete your account registration before you can refer a friend."
            type="warning"
            showIcon
          />
        </Col>
      )}
    </Row>
  )
}

// import { Alert, Card, Col, Link, Notification, PageHeader, Row, Table, Text, Tooltip, useForm } from '@app/components'
// import { QuestionCircleOutlined } from '@app/components/icons'
// import { DynamicForm, EDynamicFormField, generateDynamicFields } from '@app/modules'
// import { IStore } from '@app/redux'
// import { auth, Axios, errorHandler } from '@app/services'
// import { getThemeColors } from '@app/theme'
// import { useEffect, useMemo, useState } from 'react'
// import { useSelector } from 'react-redux'
// import styled from 'styled-components'

// type LeaderboardEntry = {
//   referralCode: string
//   referralLeadsCount: number
//   referralConversionsCount: number
// }

// type LeaderboardData = {
//   ranking: number
//   top: LeaderboardEntry[]
//   above: LeaderboardEntry[]
//   below: LeaderboardEntry[]
// }

// const limit = 3

// const StyledTable = styled(Table)<{ highlightColor: string }>`
//   tr.leaderboard-row-self {
//     background-color: ${(props) => props.highlightColor};
//     font-weight: bold;
//     td {
//       &.ant-table-cell.ant-table-cell-row-hover {
//         background-color: ${(props) => props.highlightColor};
//       }
//     }
//   }
// `

// function WalletForm({ user }: { user: IStore['user'] }) {
//   const [form] = useForm()
//   const [submitting, setSubmitting] = useState(false)

//   const initialValues = useMemo(() => {
//     return {
//       address: user.referralPayoutAddress,
//     }
//   }, [user.referralPayoutAddress])

//   const onSubmit = async () => {
//     setSubmitting(true)
//     let address
//     try {
//       const data = await form.validateFields()
//       address = data.address
//     } catch (error) {
//       // Do nothing
//       return
//     }
//     /*try {
//       await updateUser(user.uid, { referralPayoutAddress: address })
//       Notification({
//         type: 'success',
//         message: 'Wallet address saved',
//       })
//     } catch (error) {
//       errorHandler.report(error)
//       Notification({
//         type: 'error',
//         message: 'Failed to save wallet address',
//       })
//     }*/

//     try {
//       //api call to check glory referral
//       const authorization = await auth.currentUser.getIdToken()
//       const headers = {
//         Authorization: authorization,
//       }

//       const gloryCheckResponse = await Axios.post(
//         '/api/coast/glory-check',
//         { address: address },
//         {
//           headers,
//         },
//       )

//       if (gloryCheckResponse.data.verified) {
//         Notification({
//           type: 'success',
//           message: 'Glory Referral Queued',
//         })
//       }
//       Notification({
//         type: 'success',
//         message: 'Wallet address saved',
//       })
//     } catch (error) {
//       errorHandler.report(error)
//       Notification({
//         type: 'error',
//         message: 'Failed to save wallet address',
//       })
//     }

//     setSubmitting(false)
//   }

//   return (
//     <DynamicForm
//       form={form}
//       fields={generateDynamicFields(
//         [
//           {
//             inline: [
//               {
//                 name: 'address',
//                 placeholder: 'Enter wallet address',
//                 rules: [
//                   {
//                     message: 'Wallet address is required',
//                     required: true,
//                   },
//                 ],
//                 type: EDynamicFormField.INPUT,
//               },
//               {
//                 name: 'button',
//                 title: 'Save',
//                 type: EDynamicFormField.PRIMARY_BUTTON,
//               },
//             ],
//           },
//         ],
//         [
//           {
//             name: 'button',
//             loading: submitting,
//           },
//         ],
//       )}
//       initialValues={initialValues}
//       onSubmit={onSubmit}
//     />
//   )
// }

// function Leaderboard({ user }: { user: IStore['user'] }) {
//   const { uid, referralCode, referralLeadsCount, referralConversionsCount } = user

//   const [leaderboard, setLeaderboard] = useState<LeaderboardData>(null)
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)

//   const { theme } = useSelector((state: IStore) => state)
//   const colors = getThemeColors(theme.value)
//   const hihglightColor = colors.tableHighlight

//   useEffect(() => {
//     const fetch = async () => {
//       try {
//         const authorization = await auth.currentUser.getIdToken()
//         const headers = {
//           Authorization: authorization,
//         }

//         const { data } = await Axios.get('/api/referrals/leaderboard', {
//           params: {
//             uid,
//             limit,
//             orderBy: 'referralLeadsCount',
//           },
//           headers,
//         })
//         setLoading(false)
//         setLeaderboard(data)
//       } catch (error) {
//         setError(error)
//         errorHandler.report`Failed to fetch referrals leaderboard: ${error}`
//       }
//     }
//     void fetch()
//   }, [uid])

//   const leaderboardTableData = useMemo(() => {
//     if (!leaderboard) {
//       return []
//     }

//     let aboveStartsAt = 0
//     let belowStartsAt = 0
//     if (leaderboard.ranking <= limit * 2) {
//       aboveStartsAt = Math.min(limit * 2 - leaderboard.ranking + 1, leaderboard.above.length)
//     }
//     if (leaderboard.ranking <= limit) {
//       belowStartsAt = limit - leaderboard.ranking
//     }
//     const showSelf = leaderboard.ranking >= leaderboard.top.length
//     const hasGap = leaderboard.ranking > limit * 2 + 1

//     const gap = { ranking: '...', referralCode: '...', referralLeadsCount: '...', referralConversionsCount: '...' }

//     return [
//       ...leaderboard.top.map(({ referralCode, referralLeadsCount, referralConversionsCount }, idx) => ({
//         ranking: idx + 1,
//         referralCode,
//         referralLeadsCount,
//         referralConversionsCount,
//       })),
//       ...(hasGap ? [gap] : []),
//       ...leaderboard.above
//         .slice(aboveStartsAt)
//         .map(({ referralCode, referralLeadsCount, referralConversionsCount }, idx) => ({
//           ranking: leaderboard.ranking - leaderboard.above.length + idx + aboveStartsAt,
//           referralCode,
//           referralLeadsCount,
//           referralConversionsCount,
//         })),
//       ...(showSelf
//         ? [
//           {
//             ranking: leaderboard.ranking,
//             referralCode,
//             referralLeadsCount,
//             referralConversionsCount,
//           },
//         ]
//         : []),
//       ...leaderboard.below
//         .slice(belowStartsAt)
//         .map(({ referralCode, referralLeadsCount, referralConversionsCount }, idx) => ({
//           ranking: leaderboard.ranking + 1 + belowStartsAt + idx,
//           referralCode,
//           referralLeadsCount,
//           referralConversionsCount,
//         })),
//       gap,
//     ]
//   }, [leaderboard])

//   return (
//     <>
//       {!loading && error && <Text>Something went wrong. Please try again later</Text>}
//       {!error && (
//         <>
//           <Card title="Leaderboard" bodyStyle={{ padding: 0 }}>
//             <StyledTable
//               loading={loading}
//               columns={[
//                 { dataIndex: 'ranking', title: '' },
//                 {
//                   dataIndex: 'referralCode',
//                   title: 'Referral code',
//                   render: (code) => (code === referralCode ? <>You - {code}</> : code),
//                 },
//                 { dataIndex: 'referralLeadsCount', title: 'Referrals joined' },
//                 { dataIndex: 'referralConversionsCount', title: 'Referrals converted' },
//               ]}
//               rowClassName={(data) => {
//                 return data.referralCode === referralCode ? 'leaderboard-row-self' : ''
//               }}
//               dataSource={leaderboardTableData}
//               pagination={false}
//               highlightColor={hihglightColor}
//             />
//           </Card>
//         </>
//       )}
//     </>
//   )
// }

// export default function Referrals() {
//   const { user } = useSelector((state: IStore) => state)

//   const { referralCode, referralLeadsCount, referralConversionsCount, referralWaitEarned } = user

//   const origin = typeof window !== 'undefined' ? window.location.origin : ''

//   return (
//     <>
//       <Row gutter={[16, 24]}>
//         <Col span={24}>
//           <PageHeader disableBreadcrumb />
//           <Text>
//             Refer a friend and earn 5000 WAIT tokens for each friend that signs up and completes their first
//             transaction.
//           </Text>
//         </Col>
//         {referralCode && (
//           <>
//             <Col span={24}>
//               <Card title="Share your referral link">
//                 <Link href={`${origin}/invites/${referralCode}`}>
//                   <Text copyable>{`${origin}/invites/${referralCode}`}</Text>
//                 </Link>
//               </Card>
//             </Col>
//             <Col span={24}>
//               <Leaderboard user={user} />
//             </Col>
//             <Col xs={24} md={12}>
//               <Card
//                 title="Your referrals"
//                 extra={
//                   <Tooltip title="Only referrals who has completed their first Mint or Burn transaction are considered converted.">
//                     <QuestionCircleOutlined />
//                   </Tooltip>
//                 }
//               >
//                 <Text>
//                   You have referred {referralLeadsCount || 0} friend{referralLeadsCount !== 1 ? 's' : ''}
//                   {referralConversionsCount > 0 && <>, {referralConversionsCount} of them converted</>}
//                 </Text>
//               </Card>
//             </Col>
//             <Col xs={24} md={12}>
//               <Card
//                 title="Your earnings"
//                 extra={
//                   <Tooltip title="Only referrals who has completed their first transaction earn you WAIT. Please allow us a few days to process your WAIT payout.">
//                     <QuestionCircleOutlined />
//                   </Tooltip>
//                 }
//               >
//                 <Text>We've sent you {referralWaitEarned || 0} WAIT tokens</Text>
//               </Card>
//             </Col>
//             <Col xs={24} md={24}>
//               <Card title="Your wallet address for WAIT payouts">
//                 <ReferralsWalletForm user={user} />
//               </Card>
//             </Col>
//           </>
//         )}
//         {!referralCode && (
//           <Col span={24}>
//             <Alert
//               message="You must complete your account registration before you can refer a friend."
//               type="warning"
//               showIcon
//             />
//           </Col>
//         )}
//       </Row>
//     </>
//   )
// }
